// src/pages/Settings/Settings.tsx
import React, { useState, useRef, useEffect } from 'react';
import { User, Bell, Lock, Palette, Shield, Camera, Upload } from 'lucide-react';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { usersApi } from '../../api/endpoints/usersApi';
import { setUser } from '../../store/authSlice';
import { showToast } from '../../store/uiSlice';
import { AvatarCropDialog } from '../../components/common/AvatarCropDialog';
import './Settings.scss';

const Settings: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState('account');
  const [profilePhoto, setProfilePhoto] = useState('');
  
  // Account Form State
  const [formData, setFormData] = useState({
    userName: '',
    fullName: '',
    email: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (user) {
      setFormData({
        userName: user.userName || '',
        fullName: user.name || '',
        email: user.email || '',
      });
      const generatedAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || 'User')}&background=0073ea&color=fff&size=200`;
      setProfilePhoto(user.avatar || generatedAvatar);
    }
  }, [user]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so selecting the same file again still fires onChange
    event.target.value = '';
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCropImageSrc(reader.result as string);
        setCropDialogOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleCropComplete = async (blob: Blob) => {
    setCropDialogOpen(false);
    setIsUploadingPhoto(true);
    // Optimistic local preview while the upload is in flight
    const previewUrl = URL.createObjectURL(blob);
    setProfilePhoto(previewUrl);
    try {
      const updatedUser = await usersApi.uploadAvatar(blob);
      dispatch(setUser(updatedUser as any));
      setProfilePhoto(updatedUser.avatar || previewUrl);
      dispatch(showToast({ message: 'Profile photo updated', type: 'success' }));
    } catch (error: any) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || 'Failed to upload photo. Please try again.',
          type: 'error',
        })
      );
    } finally {
      setIsUploadingPhoto(false);
      setCropImageSrc(null);
    }
  };

  const tabs = [
    { id: 'account', label: 'Account', icon: <User size={20} /> },
    { id: 'notifications', label: 'Notifications (Coming Soon)', icon: <Bell size={20} /> },
    { id: 'security', label: 'Security (Coming Soon)', icon: <Lock size={20} /> },
    { id: 'preferences', label: 'Preferences (Coming Soon)', icon: <Palette size={20} /> },
    { id: 'privacy', label: 'Privacy (Coming Soon)', icon: <Shield size={20} /> },
  ];

  const handleAccountSave = async () => {
    if (!user) return;
    setIsSaving(true);
    setSaveMessage({ type: '', text: '' });
    try {
      const updatedUser = await usersApi.updateProfile({
        userName: formData.userName,
        fullName: formData.fullName,
        email: formData.email,
      } as any);
      
      // Update Redux state
      // Ensure the 'name' field is mapped properly for frontend
      const updatedName = (updatedUser as any).fullName || updatedUser.userName || updatedUser.email;
      dispatch(setUser({
        ...updatedUser,
        name: updatedName
      } as any));
      
      setSaveMessage({ type: 'success', text: 'Profile updated successfully!' });
      
      setTimeout(() => setSaveMessage({ type: '', text: '' }), 3000);
    } catch (error: any) {
      setSaveMessage({ type: 'error', text: error.message || 'Failed to update profile' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="settings-page">
      <div className="page-header">
        <h1>Settings</h1>
        <p>Manage your account settings and preferences</p>
      </div>

      <div className="settings-container">
        <div className="settings-sidebar">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="settings-content">
          {activeTab === 'account' && (
            <div className="settings-section">
              <h2>Account Settings</h2>

              {/* Profile Photo Upload */}
              <div className="profile-photo-section">
                <label>Profile Photo</label>
                <div className="photo-upload-container">
                  <div className="photo-preview" onClick={handleUploadClick} style={{ cursor: 'pointer' }}>
                    <img src={profilePhoto} alt="Profile" />
                    <div className="photo-overlay">
                      <Camera size={32} />
                    </div>
                  </div>
                  <div className="photo-upload-actions">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handlePhotoUpload}
                      style={{ display: 'none' }}
                    />
                    <button
                      className="upload-photo-btn"
                      onClick={handleUploadClick}
                      disabled={isUploadingPhoto}
                    >
                      <Upload size={18} />
                      {isUploadingPhoto ? 'Uploading…' : 'Upload Photo'}
                    </button>
                    <p className="photo-hint">
                      JPEG, PNG, or WEBP · you'll be able to crop it before saving
                    </p>
                  </div>
                </div>
              </div>

              <AvatarCropDialog
                open={cropDialogOpen}
                imageSrc={cropImageSrc}
                onClose={() => setCropDialogOpen(false)}
                onCropComplete={handleCropComplete}
              />

              <div className="setting-item">
                <label htmlFor="username">Username</label>
                <input 
                  id="username" 
                  type="text" 
                  value={formData.userName} 
                  onChange={(e) => setFormData({...formData, userName: e.target.value})} 
                />
              </div>
              <div className="setting-item">
                <label htmlFor="fullname">Full Name</label>
                <input 
                  id="fullname" 
                  type="text" 
                  value={formData.fullName}
                  onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                />
              </div>
              <div className="setting-item">
                <label htmlFor="email">Email</label>
                <input 
                  id="email" 
                  type="email" 
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
              <div className="setting-item">
                <label htmlFor="bio">Bio (Not connected)</label>
                <textarea id="bio" rows={4} defaultValue="Product Manager at Agile Edge" disabled></textarea>
              </div>
              
              {saveMessage.text && (
                <div className={`save-message ${saveMessage.type}`} style={{ 
                  color: saveMessage.type === 'error' ? 'red' : 'green', 
                  marginBottom: '1rem', 
                  fontSize: '0.9rem' 
                }}>
                  {saveMessage.text}
                </div>
              )}
              
              <button 
                className="save-button" 
                onClick={handleAccountSave}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="settings-section">
              <h2>Notification Settings</h2>
              <div className="toggle-item">
                <div className="toggle-content">
                  <h3>Email Notifications</h3>
                  <p>Receive email updates about your account</p>
                </div>
                <label className="toggle-switch">
                  <input type="checkbox" defaultChecked />
                  <span className="slider"></span>
                </label>
              </div>
              <div className="toggle-item">
                <div className="toggle-content">
                  <h3>Task Updates</h3>
                  <p>Get notified when tasks are assigned or updated</p>
                </div>
                <label className="toggle-switch">
                  <input type="checkbox" defaultChecked />
                  <span className="slider"></span>
                </label>
              </div>
              <div className="toggle-item">
                <div className="toggle-content">
                  <h3>Sprint Notifications</h3>
                  <p>Alerts about sprint deadlines and milestones</p>
                </div>
                <label className="toggle-switch">
                  <input type="checkbox" />
                  <span className="slider"></span>
                </label>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="settings-section">
              <h2>Security Settings</h2>
              <div className="setting-item">
                <label htmlFor="current-password">Current Password</label>
                <input id="current-password" type="password" />
              </div>
              <div className="setting-item">
                <label htmlFor="new-password">New Password</label>
                <input id="new-password" type="password" />
              </div>
              <div className="setting-item">
                <label htmlFor="confirm-password">Confirm Password</label>
                <input id="confirm-password" type="password" />
              </div>
              <button className="save-button">Update Password</button>
            </div>
          )}

          {activeTab === 'preferences' && (
            <div className="settings-section">
              <h2>Preferences</h2>
              <div className="setting-item">
                <label htmlFor="language">Language</label>
                <select id="language" defaultValue="en">
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                </select>
              </div>
              <div className="setting-item">
                <label htmlFor="timezone">Timezone</label>
                <select id="timezone" defaultValue="utc">
                  <option value="utc">UTC</option>
                  <option value="pst">PST</option>
                  <option value="est">EST</option>
                </select>
              </div>
              <div className="toggle-item">
                <div className="toggle-content">
                  <h3>Dark Mode</h3>
                  <p>Switch to dark theme</p>
                </div>
                <label className="toggle-switch">
                  <input type="checkbox" />
                  <span className="slider"></span>
                </label>
              </div>
              <button className="save-button">Save Preferences</button>
            </div>
          )}

          {activeTab === 'privacy' && (
            <div className="settings-section">
              <h2>Privacy Settings</h2>
              <div className="toggle-item">
                <div className="toggle-content">
                  <h3>Profile Visibility</h3>
                  <p>Make your profile visible to team members</p>
                </div>
                <label className="toggle-switch">
                  <input type="checkbox" defaultChecked />
                  <span className="slider"></span>
                </label>
              </div>
              <div className="toggle-item">
                <div className="toggle-content">
                  <h3>Activity Status</h3>
                  <p>Show when you're online</p>
                </div>
                <label className="toggle-switch">
                  <input type="checkbox" defaultChecked />
                  <span className="slider"></span>
                </label>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
